import { Box, Chip, Typography } from "@mui/material";
import type { GridColDef, GridPaginationModel, GridRowParams } from "@mui/x-data-grid";
import { useEffect, useMemo, useRef, useState } from "react";
import { useApiQuery } from "../../hooks/use-api-query.ts";
import { STATUS_COLORS } from "../../theme/theme";
import type { CheckRun } from "../../types.ts";
import { DataGrid } from "../data-grid";
import { RunStatusChip } from "../run-status-chip.tsx";
import CheckRunDetailsDrawer from "./check-run-details-drawer.tsx";

interface CheckRunsListResponse {
  runs: CheckRun[];
  total: number;
}

interface CheckRunsTableProps {
  checkId: string;
  selectedRunId?: string;
  onRunSelect?: (runId: string) => void;
  onCloseRunDrawer?: () => void;
}

export default function CheckRunsTable({
  checkId,
  selectedRunId,
  onRunSelect,
  onCloseRunDrawer,
}: CheckRunsTableProps) {
  const [selectedRun, setSelectedRun] = useState<CheckRun | null>(null);
  const processedRunIdRef = useRef<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", paginationModel.pageSize.toString());
    params.set("offset", (paginationModel.page * paginationModel.pageSize).toString());
    return `/checks/${checkId}/runs?${params.toString()}`;
  }, [checkId, paginationModel]);

  const { data: runsResponse, isLoading: isLoadingRuns } = useApiQuery<CheckRunsListResponse>(
    queryString,
    {
      enabled: !!checkId,
      refetchInterval: 5000,
    },
  );

  const runs = runsResponse?.runs ?? [];
  const totalRows = runsResponse?.total ?? 0;

  // Auto-select run when selectedRunId is provided via URL
  useEffect(() => {
    // Only process if we have a new selectedRunId that we haven't processed yet
    if (selectedRunId && selectedRunId !== processedRunIdRef.current && runs.length > 0) {
      const runToSelect = runs.find((r) => r.id === selectedRunId);
      if (runToSelect) {
        processedRunIdRef.current = selectedRunId;
        setSelectedRun(runToSelect);
      }
    }
    // Reset the ref when selectedRunId is cleared
    if (!selectedRunId) {
      processedRunIdRef.current = null;
    }
  }, [selectedRunId, runs]);

  const handleRowClick = (params: GridRowParams<CheckRun>) => {
    setSelectedRun(params.row);
    onRunSelect?.(params.row.id);
  };

  const handleCloseDrawer = () => {
    setSelectedRun(null);
    onCloseRunDrawer?.();
  };

  const columns: GridColDef<CheckRun>[] = [
    {
      field: "run_status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => <RunStatusChip status={params.value} />,
    },
    {
      field: "validation_passed",
      headerName: "Validation",
      width: 120,
      renderCell: (params) => {
        if (params.value === null || params.value === undefined) {
          return (
            <Chip
              label="N/A"
              size="small"
              sx={{
                fontSize: "0.75rem",
                backgroundColor: "grey.100",
                color: "grey.600",
              }}
            />
          );
        }
        return (
          <Chip
            label={params.value ? "Passed" : "Failed"}
            size="small"
            sx={{
              fontSize: "0.75rem",
              backgroundColor: params.value ? STATUS_COLORS.success.bg : STATUS_COLORS.error.bg,
              color: params.value ? STATUS_COLORS.success.text : STATUS_COLORS.error.text,
            }}
          />
        );
      },
    },
    {
      field: "duration_ms",
      headerName: "Duration",
      width: 120,
      valueFormatter: (value: number | null) => {
        if (value === null || value === undefined) return "-";
        if (value < 1000) return `${value}ms`;
        return `${(value / 1000).toFixed(2)}s`;
      },
    },
    {
      field: "retry_count",
      headerName: "Retries",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "trigger",
      headerName: "Trigger",
      width: 160,
      renderCell: (params) => {
        const row = params.row as CheckRun;
        if (row.trigger === "schedule" && row.check_schedule_name) {
          return (
            <Chip
              label={row.check_schedule_name}
              size="small"
              sx={{
                fontSize: "0.7rem",
                height: 20,
                maxWidth: 150,
                backgroundColor: STATUS_COLORS.info.bg,
                color: STATUS_COLORS.info.text,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
          );
        }
        return (
          <Chip
            label={params.value || "manual"}
            size="small"
            sx={{
              fontSize: "0.7rem",
              height: 20,
              backgroundColor: params.value === "schedule" ? STATUS_COLORS.info.bg : "grey.100",
              color: params.value === "schedule" ? STATUS_COLORS.info.text : "grey.700",
            }}
          />
        );
      },
    },
    {
      field: "error_message",
      headerName: "Error",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <Typography
            variant="body2"
            sx={{
              color: "error.dark",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "created_at",
      type: "dateTime",
      headerName: "Created At",
      width: 180,
      valueGetter: (val: CheckRun["created_at"]) => new Date(val),
    },
  ];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <DataGrid
        loading={isLoadingRuns}
        rows={runs}
        columns={columns}
        paginationMode="server"
        rowCount={totalRows}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[25, 50, 100]}
        onRowClick={handleRowClick}
        minHeight="calc(100vh - 300px)"
        customSx={{
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
      />

      {selectedRun && (
        <CheckRunDetailsDrawer
          open={!!selectedRun}
          onClose={handleCloseDrawer}
          checkRun={selectedRun}
          checkId={checkId}
        />
      )}
    </Box>
  );
}
